/**
 * React Query hooks for canonical composers management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CanonicalComposer,
  CanonicalComposerDetailsResponse,
  CreateCanonicalComposerInput,
  PromoteCanonicalComposerInput,
  PromoteCanonicalComposerResponse,
  UpdateCanonicalComposerInput,
} from '../types/canonical-composer'
import {
  createCanonicalComposer,
  deleteCanonicalComposer,
  getCanonicalComposerDetails,
  listCanonicalComposers,
  promoteCanonicalComposer,
  updateCanonicalComposer,
} from '../services/api/canonical-composers'

/**
 * Query key factory for canonical composers.
 */
export const canonicalComposersKeys = {
  all: ['canonical-composers'] as const,
  lists: () => [...canonicalComposersKeys.all, 'list'] as const,
  list: () => [...canonicalComposersKeys.lists()] as const,
  details: () => [...canonicalComposersKeys.all, 'detail'] as const,
  detail: (id: string) => [...canonicalComposersKeys.details(), id] as const,
}

/**
 * Hook to fetch all canonical composers.
 */
export function useCanonicalComposers() {
  return useQuery<CanonicalComposer[], Error>({
    queryKey: canonicalComposersKeys.list(),
    queryFn: listCanonicalComposers,
  })
}

/**
 * Hook to fetch canonical composer details with linked canonical pieces.
 */
export function useCanonicalComposerDetails(canonicalComposerId: string) {
  return useQuery<CanonicalComposerDetailsResponse, Error>({
    queryKey: canonicalComposersKeys.detail(canonicalComposerId),
    queryFn: () => getCanonicalComposerDetails(canonicalComposerId),
    enabled: !!canonicalComposerId,
  })
}

/**
 * Hook to create a canonical composer.
 */
export function useCreateCanonicalComposer() {
  const queryClient = useQueryClient()

  return useMutation<CanonicalComposer, Error, CreateCanonicalComposerInput>({
    mutationFn: (input) => createCanonicalComposer(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: canonicalComposersKeys.lists() })
    },
  })
}

/**
 * Hook to update a canonical composer.
 */
export function useUpdateCanonicalComposer() {
  const queryClient = useQueryClient()

  return useMutation<
    CanonicalComposer,
    Error,
    { composerId: string; input: UpdateCanonicalComposerInput }
  >({
    mutationFn: ({ composerId, input }) => updateCanonicalComposer(composerId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: canonicalComposersKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: canonicalComposersKeys.detail(variables.composerId),
      })
    },
  })
}

/**
 * Hook to delete a canonical composer.
 */
export function useDeleteCanonicalComposer() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: (composerId) => deleteCanonicalComposer(composerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: canonicalComposersKeys.lists() })
    },
  })
}

/**
 * Hook to promote a canonical composer to a Wikidata reference.
 */
export function usePromoteCanonicalComposer() {
  const queryClient = useQueryClient()

  return useMutation<
    PromoteCanonicalComposerResponse,
    Error,
    { composerId: string; input: PromoteCanonicalComposerInput }
  >({
    mutationFn: ({ composerId, input }) => promoteCanonicalComposer(composerId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: canonicalComposersKeys.lists() })
      queryClient.invalidateQueries({ queryKey: ['canonical-pieces'] })
      queryClient.invalidateQueries({ queryKey: ['freetext-suggestions'] })
    },
  })
}
